/**
 * Pure helpers for the package archive / unarchive UI.
 *
 * Kept React-free (no React, no `react-router`, no component imports) so the
 * partition + input-construction logic is unit-testable in the Node vitest
 * environment, mirroring `get-client-package-progress/entitlement.ts`,
 * `pages/client/progressView.ts`, and `pages/client/recordDelivery.ts`.
 *
 * Archive is the team's intended soft-delete (`is_active=false`), per the
 * services-packages migration's design note ("No DELETE policies anywhere
 * — soft-archive via is_active=false"). No hard delete is ever produced
 * here.
 */
import type { AgencyPackageRow, UpsertAgencyPackageInput } from '../../api/types';

export interface PartitionedPackages {
  /** Packages where `is_active = true` — the default catalog view. */
  readonly active: ReadonlyArray<AgencyPackageRow>;
  /** Packages where `is_active = false` — surfaced under the "المؤرشفة"
   *  section, never in the assignment dropdown (the EF already filters
   *  with `?active=true`). */
  readonly archived: ReadonlyArray<AgencyPackageRow>;
}

/** Split packages by `is_active`. Order within each bucket is preserved. */
export function partitionPackages(packages: ReadonlyArray<AgencyPackageRow>): PartitionedPackages {
  const active: AgencyPackageRow[] = [];
  const archived: AgencyPackageRow[] = [];
  for (const p of packages) {
    if (p.is_active) active.push(p);
    else archived.push(p);
  }
  return { active, archived };
}

/**
 * Build the `upsert-agency-package` input that flips a package to archived.
 * Only `id` and `is_active` are sent — other fields are deliberately
 * untouched (existing snapshots on `client_package_assignments` are immutable
 * and not affected by this call either way).
 */
export function buildArchiveInput(pkg: AgencyPackageRow): UpsertAgencyPackageInput {
  return { id: pkg.id, is_active: false };
}

/** Build the `upsert-agency-package` input that restores an archived package. */
export function buildUnarchiveInput(pkg: AgencyPackageRow): UpsertAgencyPackageInput {
  return { id: pkg.id, is_active: true };
}
